import { useState, useEffect } from "react";
import axios from "axios";
import { useHistory } from "react-router-dom";
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import EtatMachineCell from "./EtatMachineCell";
import { getEtatVibratoireInfo } from "./EtatMachineCell";
import "./motorsList.css";
import { API_URL } from "../constants";
import moment from "moment";
import { FaTrash } from "react-icons/fa";

// Composants extraits
import Pagination from "./Pagination";
import DeleteModal from "./DeleteModal";
import MotorCard from "./MotorCard";

// Hook data
import { useMotorsList } from "./useMotorsList";

// Helpers
import {
  getMaintenanceState,
  getPrioriteVibratoire,
  getConsommationAffichee,
} from "./motorHelpers";

const MAX_DISPLAY_LIMIT = 100;
const PRIORITE_MAINTENANCE = { "A faire": 0, "A prévoir": 1, "A jour": 2 };

export default function MotorsList(props) {
  const navigate = useHistory();

  // ─── Auth ─────────────────────────────────────────────────────────────────
  const user = localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user"))
    : props.user;
  const { token, userId } = user;
  const isAdmin = user.admin;

  // ─── Pagination state ─────────────────────────────────────────────────────
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(
    () => parseInt(localStorage.getItem("motorsPerPage")) || 10
  );
  const [options, setOptions] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");

  // ─── UI state ─────────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClient, setSelectedClient] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [hoveredRowIndex, setHoveredRowIndex] = useState(-1);
  const [isMobileView, setIsMobileView] = useState(false);
  const [expandedMotors, setExpandedMotors] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [moteurToDelete, setMoteurToDelete] = useState(null);

  // ─── Data via hook ────────────────────────────────────────────────────────
  const {
    motorsList,
    totalMotors,
    errorMessages,
    dureeFctMap,
    consoMap,
    refetch,
  } = useMotorsList({ userId, token, offset, limit });

  // ─── Responsive ──────────────────────────────────────────────────────────
  useEffect(() => {
    const check = () => setIsMobileView(window.innerWidth < 1024);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // ─── Options pagination ───────────────────────────────────────────────────
  useEffect(() => {
    if (totalMotors === 0) return;
    const step = Math.max(1, Math.ceil(totalMotors / 5));
    const generated = [];
    for (let i = step; i <= totalMotors; i += step) generated.push(i);
    if (!generated.includes(totalMotors)) generated.push(totalMotors);
    if (!generated.includes(limit)) generated.push(limit);
    setOptions(generated.sort((a, b) => a - b));
  }, [totalMotors, limit]);

  // ─── Handlers pagination ──────────────────────────────────────────────────
  const handlePreviousPage = () => offset > 0 && setOffset(offset - limit);
  const handleNextPage = () =>
    offset + limit < totalMotors && setOffset(offset + limit);

  const handleLimitChange = (e) => {
    const val = parseInt(e.target.value);
    setLimit(val);
    setOffset(0);
    localStorage.setItem("motorsPerPage", val);
  };

  const handleCustomLimitChange = (e) => {
    const val = parseInt(e.target.value, 10);
    const max = Math.min(MAX_DISPLAY_LIMIT, totalMotors);
    if (isNaN(val) || val > max || val < 1) {
      setErrorMessage(`Veuillez entrer un nombre entre 1 et ${max}.`);
      e.target.value = "";
      setTimeout(() => setErrorMessage(""), 5000);
      return;
    }
    setLimit(val);
    setOffset(0);
    setErrorMessage("");
    localStorage.setItem("motorsPerPage", val);
    if (!options.includes(val)) setOptions([...options, val].sort((a, b) => a - b));
  };

  // ─── Suppression ─────────────────────────────────────────────────────────
  const handleDeleteMachine = async () => {
    try {
      const res = await axios.delete(`${API_URL}/moteur/delete`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { id: moteurToDelete },
      });
      if (res.status === 204) {
        setShowDeleteModal(false);
        setMoteurToDelete(null);
        // Pas de window.location.reload() — on refetch proprement
        refetch();
      }
    } catch (err) {
      console.error("Erreur suppression moteur :", err.message);
    }
  };

  const handleShowDeleteModal = (id) => {
    setMoteurToDelete(id);
    setShowDeleteModal(true);
  };

  // ─── Navigation vers détail ───────────────────────────────────────────────
  const handleMotorClick = (motor) => {
    props.setSelectedMotor(motor);
    if (localStorage.getItem("user")) {
      localStorage.setItem("motor", JSON.stringify(motor));
    }
    navigate.push("/details");
  };

  // ─── Toggle ligne mobile ──────────────────────────────────────────────────
  const handleRowClick = (index) => {
    setExpandedMotors((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  // ─── Filtrage ─────────────────────────────────────────────────────────────
  const filteredMotors = motorsList.filter(
    (motor) =>
      motor.nom_moteur.toLowerCase().includes(searchTerm.toLowerCase()) &&
      (selectedClient === "" || motor.client === selectedClient) &&
      (selectedLocation === "" || motor.localisation === selectedLocation)
  );

  // ─── maintenanceMap (une seule passe) ────────────────────────────────────
  const maintenanceMap = Object.fromEntries(
    filteredMotors.map((motor) => [motor._id, getMaintenanceState(motor, motorsList)])
  );

  // ─── Tri ──────────────────────────────────────────────────────────────────
  const moteursTriés = [...filteredMotors].sort((a, b) => {
    const infoA = getEtatVibratoireInfo(a);
    const infoB = getEtatVibratoireInfo(b);
    const pA = getPrioriteVibratoire(infoA);
    const pB = getPrioriteVibratoire(infoB);

    if (pA !== pB) return pA - pB;

    if (pA >= 3) {
      const mA = PRIORITE_MAINTENANCE[maintenanceMap[a._id]] ?? 99;
      const mB = PRIORITE_MAINTENANCE[maintenanceMap[b._id]] ?? 99;
      if (mA !== mB) return mA - mB;
    }

    return (infoB.erreurCourantAnormal ?? 0) - (infoA.erreurCourantAnormal ?? 0);
  });

  const uniqueClients = [...new Set(filteredMotors.map((m) => m.client))];
  const uniqueLocations = [...new Set(filteredMotors.map((m) => m.localisation))];
  const totalPages = Math.ceil(totalMotors / limit);

  // ─── Rendu ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex flex-col text-base sm:text-3xl">
      <Header user={user} />

      <div className="flex flex-auto h-full justify-center">
        <div className="p-4 h-fit flex flex-col items-center">
          <h2 className="text-bleuSTB font-bold text-3xl xl:text-5xl 3xl:text-6xl">
            Liste des moteurs
          </h2>

          {/* ── Filtres ── */}
          <div className="flex flex-col lg:flex-row justify-between items-center mb-4">
            <input
              type="text"
              placeholder="Rechercher par nom de moteur..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="mt-4 mb-4 shadow-md w-70 sm:w-96 lg:w-80 xl:w-96 h-5 bg-white text-gray-700 outline-none text-base sm:text-lg xl:text-xl border-4 border-orangeButton hover:border-bleuSTB rounded-3xl p-6"
            />
            {isAdmin && (
              <div className="lg:ml-4 shadow-md w-52 sm:w-72 lg:w-80 xl:w-96 h-5 bg-white text-gray-700 outline-none text-base border-4 border-orangeButton hover:border-bleuSTB rounded-3xl p-6 flex items-center">
                <select
                  className="outline-none border-none text-gray-700 bg-white w-full text-base sm:text-lg xl:text-xl 3xl:text-2xl"
                  value={selectedClient}
                  onChange={(e) => {
                    setSearchTerm("");
                    setSelectedClient(e.target.value);
                  }}
                >
                  <option value="">Tous les clients</option>
                  {uniqueClients.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="mt-4 lg:mt-0 lg:ml-8 shadow-md w-70 sm:w-96 lg:w-80 xl:w-96 h-5 bg-white text-gray-700 outline-none text-base border-4 border-orangeButton hover:border-bleuSTB rounded-3xl p-6 flex items-center">
              <select
                className="outline-none border-none text-gray-700 bg-white w-full text-base sm:text-lg xl:text-xl 3xl:text-2xl"
                value={selectedLocation}
                onChange={(e) => {
                  setSearchTerm("");
                  setSelectedLocation(e.target.value);
                }}
              >
                <option value="">Toutes les localisations</option>
                {uniqueLocations.map((loc) => (
                  <option key={loc} value={loc}>{loc}</option>
                ))}
              </select>
            </div>
          </div>

          {/* ── Pagination haute ── */}
          <Pagination
            offset={offset}
            limit={limit}
            totalMotors={totalMotors}
            options={options}
            errorMessage={errorMessage}
            MAX_DISPLAY_LIMIT={MAX_DISPLAY_LIMIT}
            onPrevious={handlePreviousPage}
            onNext={handleNextPage}
            onLimitChange={handleLimitChange}
            onCustomLimitChange={handleCustomLimitChange}
          />

          {filteredMotors.length === 0 && (
            <p>Aucun moteur correspondant n'a été trouvé.</p>
          )}

          {/* ── Vue mobile ── */}
          {isMobileView ? (
            <div className="w-full md:w-96">
              {moteursTriés.map((motor, index) => (
                <MotorCard
                  key={motor._id}
                  motor={motor}
                  index={index}
                  isExpanded={expandedMotors.includes(index)}
                  onToggleExpand={handleRowClick}
                  onClick={() => handleMotorClick(motor)}
                  onDelete={handleShowDeleteModal}
                  isAdmin={isAdmin}
                  errorMessage={errorMessages[motor._id]}
                  maintenanceState={maintenanceMap[motor._id]}
                  dureeFctMap={dureeFctMap}
                  consoMap={consoMap}
                />
              ))}
            </div>
          ) : (
            /* ── Vue desktop ── */
            <table className="cursor-pointer text-xs md:text-sm lg:text-base xl:text-xl 2xl:text-2xl 3xl:text-3xl">
              <thead className="hidden lg:contents">
                <tr>
                  {["Date de mise en service", "Moteur", ...(isAdmin ? ["Client"] : []), "Localisation", "Consommation", "DureeFct", "Maintenance", "Etat de la machine"].map((h) => (
                    <th key={h} scope="col" className="bg-bleuCielSTB text-white p-2 text-center border-2">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {moteursTriés.map((motor, index) => {
                  const errMsg = errorMessages[motor._id];
                  const maintenanceState = maintenanceMap[motor._id];
                  const conso = getConsommationAffichee(motor, consoMap);

                  return (
                    <tr
                      key={motor._id}
                      className={`flex flex-col mb-10 ${index === hoveredRowIndex ? "bg-hoverTable" : ""} ${errMsg ? "bg-red-200" : ""} lg:table-row`}
                      onClick={() => handleMotorClick(motor)}
                      onMouseEnter={() => setHoveredRowIndex(index)}
                      onMouseLeave={() => setHoveredRowIndex(-1)}
                      title={errMsg}
                    >
                      {/* Cellule helper pour éviter la répétition du label mobile */}
                      {[
                        { label: "Date de mise en service", content: moment(motor.dateMiseEnService).format("DD/MM/YYYY") },
                        { label: "Moteur", content: <span className="text-marronSTB font-bold">{motor.nom_moteur}</span> },
                        ...(isAdmin ? [{ label: "Client", content: motor.client }] : []),
                        { label: "Localisation", content: motor.localisation },
                        // Espace insécable : l'unité reste sur la même ligne que la valeur, même avec 5 chiffres
                        { label: "Consommation", content: `${conso}\u00A0kWh` },
                        { label: "DureeFct", content: `${parseInt(dureeFctMap[motor._id]?.toFixed(0))}\u00A0h` },
                      ].map(({ label, content }) => (
                        <td key={label} className="text-end border-2 border-bleuCielSTB p-4 lg:text-center">
                          <span className="lg:hidden" style={{ float: "left", fontWeight: 500, color: "#f48507", marginRight: "5em" }}>
                            {label}
                          </span>
                          {content}
                        </td>
                      ))}

                      {/* Maintenance */}
                      <td
                        className={`text-end border-2 border-bleuCielSTB p-4 lg:text-center ${
                          maintenanceState === "A faire" ? "bg-red-200" : maintenanceState === "A prévoir" ? "bg-orange-200" : "bg-green-200"
                        }`}
                      >
                        <span className="lg:hidden" style={{ float: "left", fontWeight: 500, color: "#f48507", marginRight: "5em" }}>
                          Maintenance
                        </span>
                        <span className={maintenanceState === "A faire" ? "text-red-700" : maintenanceState === "A prévoir" ? "text-orange-700" : "text-green-700"}>
                          {maintenanceState}
                        </span>
                      </td>

                      {/* Etat machine */}
                      <td className="text-end border-2 border-bleuCielSTB p-4 lg:text-center">
                        <EtatMachineCell motor={motor} />
                      </td>

                      {/* Suppression (admin) */}
                      {isAdmin && (
                        <td className="text-end border-2 border-bleuCielSTB p-4 lg:text-center">
                          <button
                            className="text-bleuSTB hover:text-marronSTB text-3xl cursor-pointer"
                            onClick={(e) => { e.stopPropagation(); handleShowDeleteModal(motor._id); }}
                          >
                            <FaTrash />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* ── Pagination basse ── */}
          <div className="flex justify-between items-center w-full mt-4 space-x-6">
            <button
              onClick={handlePreviousPage}
              disabled={offset === 0}
              className={`px-6 py-3 text-xs sm:text-base font-medium rounded-full ${offset === 0 ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-bleuSTB text-white hover:bg-marronSTB"}`}
            >
              Précédent
            </button>
            <span className="text-gray-600 text-xs sm:text-base font-semibold">
              Page {Math.floor(offset / limit) + 1} sur {totalPages}
            </span>
            <button
              onClick={handleNextPage}
              disabled={offset + limit >= totalMotors}
              className={`px-6 py-3 text-xs sm:text-base font-medium rounded-full ${offset + limit >= totalMotors ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-bleuSTB text-white hover:bg-marronSTB"}`}
            >
              Suivant
            </button>
          </div>
        </div>
      </div>

      <Footer />

      {showDeleteModal && (
        <DeleteModal
          onConfirm={handleDeleteMachine}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
} 